import type {
  AttachmentUploadInput,
  BootstrapView,
  CursorPage,
  EventPage,
  InboxFuryWebPort,
  RequestContext,
  SecretReveal,
  ThreadPage,
  ThreadView,
} from "./ports";
import { ApiProblem } from "./ports";
import type {
  ApiClientView,
  QueueStatView,
  Tenant,
  AccountSessionView,
  AttachmentUploadView,
  Draft,
  DraftSeed,
  DeletionIntentCreationView,
  DeletionIntentInput,
  DeletionIntentView,
  AliasView,
  DnsRecordRequirement,
  DomainChallengeView,
  FolderView,
  LabelView,
  MailboxBatchView,
  Mailbox,
  MembershipView,
  ExportCreateInput,
  ExportCreationView,
  ExportDownloadGrantView,
  ExportView,
  OperationView,
  ScheduledSendView,
  SessionView,
  SignatureView,
  VacationPolicyView,
  WebhookDeliveryView,
  WebhookView,
  ThreadSummary,
  TotpEnrollmentView,
} from "../domain/types";
import type { MailSearch } from "../domain/search";

type FetchPort = typeof fetch;

interface RequestOptions extends RequestContext {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  idempotencyKey?: string;
  ifMatch?: string;
  csrf?: boolean;
  csrfRetried?: boolean;
}

function isMutation(method: RequestOptions["method"]): boolean {
  return method !== undefined && method !== "GET";
}

export class SameOriginBffClient implements InboxFuryWebPort {
  #csrfToken?: Promise<string>;

  constructor(private readonly fetchPort: FetchPort = window.fetch.bind(window)) {}

  async #csrf(signal?: AbortSignal): Promise<string> {
    if (!this.#csrfToken) {
      this.#csrfToken = this.fetchPort("/session/csrf", {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        headers: { Accept: "application/json" },
        signal,
      }).then(async (response) => {
        if (!response.ok) throw await this.#problem(response);
        const data = (await response.json()) as { csrf_token?: unknown };
        if (typeof data.csrf_token !== "string" || data.csrf_token.length < 32) {
          throw new ApiProblem(500, "invalid_csrf_contract", "The secure session boundary returned an invalid CSRF token.", response.headers.get("x-request-id") ?? "unknown");
        }
        return data.csrf_token;
      });
    }
    return this.#csrfToken;
  }

  async #sessionRequest<T>(path: "/session" | "/session/claim" | "/session/reauthenticate", options: RequestOptions = {}): Promise<T> {
    const method = options.method ?? "GET";
    const headers: Record<string, string> = { Accept: "application/json" };
    if (options.body !== undefined) headers["Content-Type"] = "application/json";
    if (isMutation(method)) headers["X-CSRF-Token"] = await this.#csrf(options.signal);
    const response = await this.fetchPort(path, {
      method,
      credentials: "same-origin",
      cache: "no-store",
      redirect: "error",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
    if (!response.ok) {
      return this.#recoverMutation(path, options, method, response);
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  async #problem(response: Response): Promise<ApiProblem> {
    let data: Record<string, unknown> = {};
    if (response.headers.get("content-type")?.includes("json")) {
      try { data = (await response.json()) as Record<string, unknown>; } catch { /* use the safe fallback */ }
    }
    return new ApiProblem(
      response.status,
      typeof data.type === "string" ? data.type : "request_failed",
      typeof data.detail === "string" ? data.detail : "InboxFury could not complete the request.",
      typeof data.request_id === "string" ? data.request_id : response.headers.get("x-request-id") ?? "unknown",
      typeof data.field_errors === "object" && data.field_errors ? data.field_errors as Record<string, string> : {},
    );
  }

  async #request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    if (!path.startsWith("/bff/v1/")) throw new Error("BFF requests must use an exact /bff/v1 route.");
    const method = options.method ?? "GET";
    const headers: Record<string, string> = { Accept: "application/json" };
    if (options.body !== undefined) headers["Content-Type"] = "application/json";
    if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
    if (options.ifMatch) headers["If-Match"] = options.ifMatch;
    if (isMutation(method) && options.csrf !== false) headers["X-CSRF-Token"] = await this.#csrf(options.signal);
    const response = await this.fetchPort(path, {
      method,
      credentials: "same-origin",
      cache: "no-store",
      redirect: "error",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
    if (!response.ok) {
      return this.#recoverMutation(path, options, method, response);
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  async #recoverMutation<T>(
    path: string,
    options: RequestOptions,
    method: RequestOptions["method"],
    response: Response,
  ): Promise<T> {
    const problem = await this.#problem(response);
    if (response.status === 403 && isMutation(method)) this.#csrfToken = undefined;
    if (
      response.status === 403 &&
      isMutation(method) &&
      options.csrf !== false &&
      !options.csrfRetried &&
      problem.type === "csrf_failed"
    ) {
      return path === "/session" || path === "/session/claim" || path === "/session/reauthenticate"
        ? this.#sessionRequest<T>(path, { ...options, csrfRetried: true })
        : this.#request<T>(path, { ...options, csrfRetried: true });
    }
    throw problem;
  }

  async bootstrap(context?: RequestContext): Promise<BootstrapView> {
    return this.#request<BootstrapView>("/bff/v1/bootstrap", context);
  }


  async getSession(context?: RequestContext): Promise<SessionView> {
    return mapSession(await this.#sessionRequest<WireSession>("/session", context));
  }

  async signIn(input: { tenantId: string; email: string; password: string; totpCode?: string }, context?: RequestContext): Promise<SessionView> {
    const session = await this.#sessionRequest<WireSession>("/session", {
      ...context,
      method: "POST",
      body: {
        tenant_id: input.tenantId,
        email: input.email,
        password: input.password,
        ...(input.totpCode !== undefined ? { totp_code: input.totpCode } : {}),
      },
    });
    this.#csrfToken = undefined;
    return mapSession(session);
  }

  async signOut(context?: RequestContext): Promise<void> {
    await this.#sessionRequest<void>("/session", { ...context, method: "DELETE" });
    this.#csrfToken = undefined;
  }

  async reauthenticate(input: { password: string; totpCode: string }, context?: RequestContext): Promise<SessionView> {
    return mapSession(await this.#sessionRequest<WireSession>("/session/reauthenticate", {
      ...context,
      method: "POST",
      body: { password: input.password, totp_code: input.totpCode },
    }));
  }

  async listAccountSessions(context?: RequestContext): Promise<AccountSessionView[]> {
    const result = await this.#request<{ data: WireAccountSession[] }>("/bff/v1/account/sessions", context);
    return result.data.map(mapAccountSession);
  }

  revokeAccountSession(sessionId: string, context?: RequestContext): Promise<void> {
    return this.#request(`/bff/v1/account/sessions/${encodeURIComponent(sessionId)}`, { ...context, method: "DELETE" });
  }

  async changePassword(input: { currentPassword: string; totpCode: string; newPassword: string }, context?: RequestContext): Promise<SessionView> {
    return mapSession(await this.#request<WireSession>("/bff/v1/account/password", {
      ...context,
      method: "PATCH",
      body: { current_password: input.currentPassword, totp_code: input.totpCode, new_password: input.newPassword },
    }));
  }

  async beginTotpEnrollment(context?: RequestContext): Promise<TotpEnrollmentView> {
    const result = await this.#request<{ enrollment_id: string; totp_secret: string }>("/bff/v1/account/totp/enrollment", { ...context, method: "POST", body: {} });
    return { enrollmentId: result.enrollment_id, totpSecret: result.totp_secret };
  }

  async verifyTotpEnrollment(enrollmentId: string, totpCode: string, context?: RequestContext): Promise<{ session: SessionView; recoveryCodes: string[] }> {
    const result = await this.#request<WireSession & { recovery_codes: string[] }>(`/bff/v1/account/totp/enrollment/${encodeURIComponent(enrollmentId)}/verify`, {
      ...context,
      method: "POST",
      body: { totp_code: totpCode },
    });
    return { session: mapSession(result), recoveryCodes: result.recovery_codes };
  }

  async revokeAccount(confirmation: "REVOKE INBOXFURY ACCOUNT", context?: RequestContext): Promise<void> {
    await this.#request<void>("/bff/v1/account/revoke", { ...context, method: "POST", body: { confirmation } });
    this.#csrfToken = undefined;
  }

  listThreads(search: MailSearch, cursor?: string, context?: RequestContext): Promise<ThreadPage> {
    const params = new URLSearchParams();
    if (search.text.length) params.set("q", search.text.join(" "));
    for (const key of ["from", "to", "subject", "folder", "after", "before"] as const) {
      const value = search[key];
      if (value) params.set(key, value);
    }
    // Repeated rather than comma-joined: a mailbox address or a domain name may
    // itself contain a comma-adjacent character, and repetition is the one form
    // every server-side parser reads the same way.
    for (const mailbox of search.mailboxes ?? []) params.append("mailbox", mailbox);
    for (const domain of search.domains ?? []) params.append("domain", domain);
    if (search.hasAttachment) params.set("has_attachment", "true");
    if (search.unread) params.set("unread", "true");
    if (search.starred) params.set("starred", "true");
    if (cursor) params.set("cursor", cursor);
    return this.#request<ThreadPage>(`/bff/v1/threads?${params.toString()}`, context);
  }

  getThread(threadId: string, context?: RequestContext): Promise<ThreadView> {
    return this.#request<ThreadView>(`/bff/v1/threads/${encodeURIComponent(threadId)}`, context);
  }

  updateThreadState(threadId: string, update: { unread?: boolean; starred?: boolean; folder?: string }, version: number, context?: RequestContext): Promise<ThreadSummary> {
    return this.#request(`/bff/v1/threads/${encodeURIComponent(threadId)}`, { ...context, method: "PATCH", body: update, ifMatch: `"thread-${version}"` });
  }

  createDraft(draft: DraftSeed, idempotencyKey: string, context?: RequestContext): Promise<Draft> {
    return this.#request("/bff/v1/drafts", { ...context, method: "POST", body: draft, idempotencyKey });
  }

  saveDraft(draft: Draft, ifMatch: string, context?: RequestContext): Promise<Draft> {
    return this.#request(`/bff/v1/drafts/${encodeURIComponent(draft.id)}`, { ...context, method: "PATCH", body: draft, ifMatch });
  }

  submitDraft(draftId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView> {
    return this.#request(`/bff/v1/drafts/${encodeURIComponent(draftId)}/submit`, { ...context, method: "POST", body: {}, idempotencyKey });
  }

  listScheduledSends(mailboxId: string, cursor?: string, context?: RequestContext): Promise<CursorPage<ScheduledSendView>> {
    const params = new URLSearchParams({ mailbox_id: mailboxId, state: "scheduled" });
    if (cursor) params.set("cursor", cursor);
    return this.#request(`/bff/v1/send-intents?${params.toString()}`, context);
  }

  listOutboundSends(mailboxId: string, cursor?: string, context?: RequestContext): Promise<CursorPage<ScheduledSendView>> {
    const params = new URLSearchParams({ mailbox_id: mailboxId });
    if (cursor) params.set("cursor", cursor);
    return this.#request(`/bff/v1/send-intents?${params.toString()}`, context);
  }

  cancelScheduledSend(sendIntentId: string, ifMatch: string, context?: RequestContext): Promise<OperationView> {
    return this.#request(`/bff/v1/send-intents/${encodeURIComponent(sendIntentId)}/cancel`, { ...context, method: "POST", body: {}, ifMatch, idempotencyKey: crypto.randomUUID() });
  }


  async uploadAttachment(input: AttachmentUploadInput, idempotencyKey: string, context?: RequestContext): Promise<AttachmentUploadView> {
    const initiated = await this.#request<AttachmentUploadView & { upload: { url: string; method: "PUT"; headers: Record<string, string>; expiresAt: string; maxBytes: number } }>(
      "/bff/v1/uploads",
      {
        ...context,
        method: "POST",
        body: {
          draftId: input.draftId,
          fileName: input.file.name,
          mediaType: input.file.type || "application/octet-stream",
          sizeBytes: input.file.size,
        },
        idempotencyKey,
      },
    );
    const target = safeTransferUrl(initiated.upload.url);
    const grantExpiry = new Date(initiated.upload.expiresAt).getTime();
    if (
      initiated.upload.method !== "PUT"
      || !Number.isFinite(initiated.upload.maxBytes)
      || initiated.upload.maxBytes < 0
      || input.file.size > initiated.upload.maxBytes
      || !Number.isFinite(grantExpiry)
      || grantExpiry <= Date.now()
    ) {
      throw new ApiProblem(409, "invalid_upload_contract", "The attachment upload contract could not safely accept this file.", "browser");
    }
    const uploadHeaders = safeUploadHeaders(initiated.upload.headers);
    const uploaded = await this.fetchPort(target, {
      method: "PUT",
      credentials: "omit",
      redirect: "error",
      referrerPolicy: "no-referrer",
      headers: uploadHeaders,
      body: input.file,
      signal: context?.signal,
    });
    if (!uploaded.ok) throw await this.#problem(uploaded);
    return this.#request(`/bff/v1/uploads/${encodeURIComponent(initiated.id)}/complete`, {
      ...context,
      method: "POST",
      body: {},
      idempotencyKey: `${idempotencyKey}.complete`,
    });
  }

  getAttachmentUpload(uploadId: string, context?: RequestContext): Promise<AttachmentUploadView> {
    return this.#request(`/bff/v1/uploads/${encodeURIComponent(uploadId)}`, context);
  }

  async createAttachmentDownloadGrant(attachmentId: string, idempotencyKey: string, context?: RequestContext): Promise<{ url: string; expiresAt: string }> {
    const result = await this.#request<{ url: string; expiresAt: string }>(
      `/bff/v1/attachments/${encodeURIComponent(attachmentId)}/download-grants`,
      { ...context, method: "POST", body: {}, idempotencyKey },
    );
    safeTransferUrl(result.url);
    return result;
  }

  pollEvents(after: string, context?: RequestContext): Promise<EventPage> {
    const params = new URLSearchParams({ after, wait: "25" });
    return this.#request(`/bff/v1/events?${params.toString()}`, context);
  }

  getSignature(mailboxId: string, context?: RequestContext): Promise<SignatureView> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}/signature`, context);
  }

  saveSignature(mailboxId: string, input: { senderIdentityId: string; plainText: string }, ifMatch: string, context?: RequestContext): Promise<SignatureView> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}/signature`, { ...context, method: "PATCH", body: input, ifMatch });
  }

  getVacationPolicy(mailboxId: string, context?: RequestContext): Promise<VacationPolicyView> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}/vacation-policy`, context);
  }

  saveVacationPolicy(mailboxId: string, input: Omit<VacationPolicyView, "id" | "mailboxId" | "version" | "etag">, ifMatch: string, context?: RequestContext): Promise<VacationPolicyView> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}/vacation-policy`, { ...context, method: "PATCH", body: input, ifMatch });
  }

  requestDomain(name: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView> {
    return this.#request("/bff/v1/domains", { ...context, method: "POST", body: { name }, idempotencyKey });
  }

  listDnsRequirements(domainId: string, context?: RequestContext): Promise<DnsRecordRequirement[]> {
    return this.#request(`/bff/v1/domains/${encodeURIComponent(domainId)}/dns-requirements`, context);
  }

  listDomainChallenges(domainId: string, context?: RequestContext): Promise<DomainChallengeView[]> {
    return this.#request(`/bff/v1/domains/${encodeURIComponent(domainId)}/challenges`, context);
  }

  getMailbox(mailboxId: string, context?: RequestContext): Promise<Mailbox> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}`, context);
  }

  updateMailbox(
    mailboxId: string,
    changes: { displayName?: string; quotaBytes?: number; sendingEnabled?: boolean; receivingEnabled?: boolean },
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<OperationView> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}`, {
      ...context,
      method: "PATCH",
      // Only what changed is sent. An absent field means "leave it alone", and
      // sending the current value for everything would overwrite anything
      // changed between reading the mailbox and saving it.
      body: {
        ...(changes.displayName === undefined ? {} : { display_name: changes.displayName }),
        ...(changes.quotaBytes === undefined ? {} : { quota_bytes: changes.quotaBytes }),
        ...(changes.sendingEnabled === undefined ? {} : { sending_enabled: changes.sendingEnabled }),
        ...(changes.receivingEnabled === undefined ? {} : { receiving_enabled: changes.receivingEnabled }),
      },
      idempotencyKey,
    });
  }

  rotateMailboxCredentials(mailboxId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}/credential-rotations`, {
      ...context,
      method: "POST",
      body: {},
      idempotencyKey,
    });
  }

  createMailboxBatch(
    mailboxes: ReadonlyArray<{ domainId: string; localPart: string; quotaBytes?: number }>,
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<MailboxBatchView> {
    return this.#request("/bff/v1/mailbox-batches", {
      ...context,
      method: "POST",
      body: {
        mailboxes: mailboxes.map((mailbox) => ({
          domain_id: mailbox.domainId,
          local_part: mailbox.localPart,
          ...(mailbox.quotaBytes === undefined ? {} : { quota_bytes: mailbox.quotaBytes }),
        })),
      },
      idempotencyKey,
    });
  }

  getMailboxBatch(batchId: string, context?: RequestContext): Promise<MailboxBatchView> {
    return this.#request(`/bff/v1/mailbox-batches/${encodeURIComponent(batchId)}`, context);
  }

  listFolders(mailboxId: string, context?: RequestContext): Promise<FolderView[]> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}/folders`, context);
  }

  listLabels(mailboxId: string, context?: RequestContext): Promise<LabelView[]> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}/labels`, context);
  }

  listAliases(context?: RequestContext): Promise<AliasView[]> {
    return this.#request("/bff/v1/aliases", context);
  }

  getAlias(aliasId: string, context?: RequestContext): Promise<AliasView> {
    return this.#request(`/bff/v1/aliases/${encodeURIComponent(aliasId)}`, context);
  }

  createAlias(input: { address: string; destinationMailboxIds: string[] }, idempotencyKey: string, context?: RequestContext): Promise<OperationView> {
    return this.#request("/bff/v1/aliases", {
      ...context,
      method: "POST",
      body: { address: input.address, destination_mailbox_ids: input.destinationMailboxIds },
      idempotencyKey,
    });
  }

  /**
   * Queue depth and recent throughput for this tenancy.
   *
   * Counts, not rows: an operator wants to know whether work is moving, and a
   * list of operation ids does not answer that.
   */
  /** Save the workspace name, postal address and timezone. */
  updateTenantProfile(
    input: { displayName?: string; physicalAddress?: string; timezone?: string },
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<Tenant> {
    return this.#request("/bff/v1/tenant/profile", {
      ...context, method: "PATCH", idempotencyKey,
      body: {
        ...(input.displayName === undefined ? {} : { display_name: input.displayName }),
        ...(input.physicalAddress === undefined ? {} : { physical_address: input.physicalAddress }),
        ...(input.timezone === undefined ? {} : { timezone: input.timezone }),
      },
    });
  }

  listQueueStats(context?: RequestContext): Promise<{ queues: QueueStatView[]; as_of: string }> {
    return this.#request("/bff/v1/operations/queue-stats", { ...context, method: "GET" });
  }

  getOperation(operationId: string, context?: RequestContext): Promise<OperationView> {
    return this.#request(`/bff/v1/operations/${encodeURIComponent(operationId)}`, context);
  }

  listMemberships(context?: RequestContext): Promise<MembershipView[]> {
    return this.#request("/bff/v1/memberships", context);
  }

  revokeMembership(membershipId: string, context?: RequestContext): Promise<void> {
    return this.#request(`/bff/v1/memberships/${encodeURIComponent(membershipId)}/revoke`, {
      ...context,
      method: "POST",
      body: {},
    });
  }

  requestMailbox(domainId: string, localPart: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView> {
    return this.#request("/bff/v1/mailboxes", { ...context, method: "POST", body: { domain_id: domainId, local_part: localPart, provider: "hosted_mailcow" }, idempotencyKey });
  }

  adoptDomain(domainId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView> {
    return this.#request(`/bff/v1/domains/${encodeURIComponent(domainId)}/adopt`, {
      ...context,
      method: "POST",
      body: {},
      idempotencyKey,
    });
  }

  adoptMailbox(mailboxId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView> {
    return this.#request(`/bff/v1/mailboxes/${encodeURIComponent(mailboxId)}/adopt`, {
      ...context,
      method: "POST",
      body: {},
      idempotencyKey,
    });
  }

  async createApiClient(input: { name: string; scopes: string[]; mailboxRestrictions: string[] }, idempotencyKey: string, context?: RequestContext): Promise<SecretReveal<ApiClientView>> {
    const resource = await this.#request<ApiClientView>("/bff/v1/api-clients", { ...context, method: "POST", body: { name: input.name, scopes: input.scopes, mailbox_restrictions: input.mailboxRestrictions }, idempotencyKey });
    const reveal = await this.#request<{ secret: string }>(`/bff/v1/api-clients/${encodeURIComponent(resource.id)}/keys`, { ...context, method: "POST", body: {}, idempotencyKey: `${idempotencyKey}.key` });
    return { resource, secret: reveal.secret, recoverable: false };
  }

  revokeApiClient(id: string, context?: RequestContext): Promise<ApiClientView> {
    return this.#request(`/bff/v1/api-clients/${encodeURIComponent(id)}/revoke`, { ...context, method: "POST", body: {}, idempotencyKey: crypto.randomUUID() });
  }

  createWebhook(input: { endpoint: string; eventPatterns: string[] }, idempotencyKey: string, context?: RequestContext): Promise<SecretReveal<WebhookView>> {
    return this.#request("/bff/v1/webhooks", { ...context, method: "POST", body: { endpoint: input.endpoint, event_patterns: input.eventPatterns }, idempotencyKey });
  }


  listWebhookDeliveries(webhookId: string, cursor?: string, context?: RequestContext): Promise<CursorPage<WebhookDeliveryView>> {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    return this.#request(`/bff/v1/webhooks/${encodeURIComponent(webhookId)}/deliveries?${params.toString()}`, context);
  }

  replayWebhookDelivery(webhookId: string, deliveryId: string, idempotencyKey: string, context?: RequestContext): Promise<OperationView> {
    return this.#request(`/bff/v1/webhooks/${encodeURIComponent(webhookId)}/deliveries/${encodeURIComponent(deliveryId)}/replay`, { ...context, method: "POST", body: {}, idempotencyKey });
  }

  async listExports(cursor?: string, context?: RequestContext): Promise<CursorPage<ExportView>> {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    const result = asRecord(await this.#request<unknown>(
      `/bff/v1/exports${params.size ? `?${params.toString()}` : ""}`,
      context,
    ));
    if (!Array.isArray(result.data)) throw invalidGovernanceContract("The server returned an invalid export page.");
    return {
      data: result.data.map(mapExportView),
      ...(typeof result.nextCursor === "string" && result.nextCursor ? { nextCursor: result.nextCursor } : {}),
    };
  }

  async createExport(input: ExportCreateInput, idempotencyKey: string, context?: RequestContext): Promise<ExportCreationView> {
    const body = input.scope === "mailbox"
      ? { scope: input.scope, mailbox_id: input.mailboxId, format: input.format, content_mode: input.contentMode }
      : { scope: input.scope, format: input.format, content_mode: input.contentMode };
    const result = asRecord(await this.#request<unknown>("/bff/v1/exports", {
      ...context,
      method: "POST",
      body,
      idempotencyKey,
    }));
    return { resource: mapExportView(result.resource), operation: mapOperationView(result.operation) };
  }

  async getExport(exportId: string, context?: RequestContext): Promise<ExportView> {
    return mapExportView(await this.#request<unknown>(
      `/bff/v1/exports/${encodeURIComponent(exportId)}`,
      context,
    ));
  }

  async createExportDownloadGrant(exportId: string, idempotencyKey: string, context?: RequestContext): Promise<ExportDownloadGrantView> {
    const result = asRecord(await this.#request<unknown>(
      `/bff/v1/exports/${encodeURIComponent(exportId)}/download-grants`,
      { ...context, method: "POST", body: {}, idempotencyKey },
    ));
    const method = requiredString(result, "method");
    const headers = asRecord(result.headers ?? {});
    const mediaType = requiredString(result, "mediaType");
    const sizeBytes = Number(result.sizeBytes);
    const expiresAt = requiredString(result, "expiresAt");
    const expiresAtMillis = new Date(expiresAt).getTime();
    if (
      method !== "GET"
      || Object.keys(headers).length !== 0
      || !["application/x-ndjson", "application/x-tar"].includes(mediaType)
      || !Number.isSafeInteger(sizeBytes)
      || sizeBytes < 0
      || !Number.isFinite(expiresAtMillis)
      || expiresAtMillis <= Date.now()
    ) {
      throw invalidGovernanceContract("The server returned an invalid export download grant.");
    }
    return {
      url: safeTransferUrl(requiredString(result, "url")),
      method,
      headers: {},
      expiresAt,
      fileName: requiredString(result, "fileName"),
      mediaType: mediaType as ExportDownloadGrantView["mediaType"],
      sizeBytes,
    };
  }

  async listDeletionIntents(cursor?: string, context?: RequestContext): Promise<CursorPage<DeletionIntentView>> {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    const result = asRecord(await this.#request<unknown>(
      `/bff/v1/deletion-intents${params.size ? `?${params.toString()}` : ""}`,
      context,
    ));
    if (!Array.isArray(result.data)) throw invalidGovernanceContract("The server returned an invalid deletion-intent page.");
    return {
      data: result.data.map(mapDeletionIntentView),
      ...(typeof result.nextCursor === "string" && result.nextCursor ? { nextCursor: result.nextCursor } : {}),
    };
  }

  async createDeletionIntent(input: DeletionIntentInput, idempotencyKey: string, context?: RequestContext): Promise<DeletionIntentCreationView> {
    const result = asRecord(await this.#request<unknown>("/bff/v1/deletion-intents", {
      ...context,
      method: "POST",
      body: { resource_type: input.resourceType, resource_id: input.resourceId },
      idempotencyKey,
    }));
    return { resource: mapDeletionIntentView(result.resource), operation: mapOperationView(result.operation) };
  }

  async getDeletionIntent(deletionIntentId: string, context?: RequestContext): Promise<DeletionIntentView> {
    return mapDeletionIntentView(await this.#request<unknown>(
      `/bff/v1/deletion-intents/${encodeURIComponent(deletionIntentId)}`,
      context,
    ));
  }

  async confirmDeletionIntent(
    deletionIntentId: string,
    confirmationDigest: string,
    ifMatch: string,
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<DeletionIntentCreationView> {
    const result = asRecord(await this.#request<unknown>(
      `/bff/v1/deletion-intents/${encodeURIComponent(deletionIntentId)}/confirm`,
      {
        ...context,
        method: "POST",
        body: { confirmation_digest: confirmationDigest },
        ifMatch,
        idempotencyKey,
      },
    ));
    return { resource: mapDeletionIntentView(result.resource), operation: mapOperationView(result.operation) };
  }

  async cancelDeletionIntent(
    deletionIntentId: string,
    ifMatch: string,
    idempotencyKey: string,
    context?: RequestContext,
  ): Promise<DeletionIntentView> {
    return mapDeletionIntentView(await this.#request<unknown>(
      `/bff/v1/deletion-intents/${encodeURIComponent(deletionIntentId)}/cancel`,
      { ...context, method: "POST", body: {}, ifMatch, idempotencyKey },
    ));
  }

  createBillingPortalSession(returnPath: string, idempotencyKey: string, context?: RequestContext): Promise<{ redirectUrl: string; expiresAt: string }> {
    if (!returnPath.startsWith("/") || returnPath.startsWith("//")) throw new Error("Billing return path must be same-origin.");
    return this.#request("/bff/v1/billing/portal-sessions", { ...context, method: "POST", body: { returnPath }, idempotencyKey });
  }
}

interface WireSession {
  tenant_id: string;
  user_id: string;
  role: SessionView["role"];
  grants: Array<{ permission: string; resource_type: "tenant" | "domain" | "mailbox"; resource_id: string | null }>;
  recent_authentication_at: string;
  totp_enrolled?: boolean;
  identity_subject?: string;
}

interface WireAccountSession {
  id: string;
  authenticated_at: string;
  recent_authentication_at: string;
  last_seen_at: string;
  idle_expires_at: string;
  absolute_expires_at: string;
  current: boolean;
}

function mapSession(value: WireSession): SessionView {
  return {
    tenantId: value.tenant_id,
    userId: value.user_id,
    role: value.role,
    grants: value.grants.map((grant) => ({ permission: grant.permission, resourceType: grant.resource_type, resourceId: grant.resource_id })),
    recentAuthenticationAt: value.recent_authentication_at,
    // Absent is treated as not enrolled, which is the safe direction: it offers
    // enrolment to someone who already has it (recoverable, and they will
    // notice) rather than withholding it from someone who does not.
    totpEnrolled: value.totp_enrolled === true,
    ...(typeof value.identity_subject === "string" && value.identity_subject.trim()
      ? { identitySubject: value.identity_subject }
      : {}),
  };
}

function mapAccountSession(value: WireAccountSession): AccountSessionView {
  return {
    id: value.id,
    authenticatedAt: value.authenticated_at,
    recentAuthenticationAt: value.recent_authentication_at,
    lastSeenAt: value.last_seen_at,
    idleExpiresAt: value.idle_expires_at,
    absoluteExpiresAt: value.absolute_expires_at,
    current: value.current,
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw invalidGovernanceContract("The server returned an invalid governance response.");
  }
  return value as Record<string, unknown>;
}

function requiredString(value: Record<string, unknown>, key: string): string {
  const field = value[key];
  if (typeof field !== "string" || field.length === 0) {
    throw invalidGovernanceContract("The server returned an incomplete governance response.");
  }
  return field;
}

function requiredVersion(value: Record<string, unknown>): number {
  const version = Number(value.version);
  if (!Number.isSafeInteger(version) || version < 1) {
    throw invalidGovernanceContract("The server returned an invalid governance version.");
  }
  return version;
}

function requiredBoolean(value: Record<string, unknown>, key: string): boolean {
  const field = value[key];
  if (typeof field !== "boolean") throw invalidGovernanceContract("The server returned an invalid governance flag.");
  return field;
}

function requiredCount(value: Record<string, unknown>, key: string): number {
  const field = Number(value[key]);
  if (!Number.isSafeInteger(field) || field < 0) throw invalidGovernanceContract("The server returned an invalid governance count.");
  return field;
}

function nullableString(value: Record<string, unknown>, key: string): string | null {
  const field = value[key];
  if (field === null) return null;
  if (typeof field !== "string" || field.length === 0) throw invalidGovernanceContract("The server returned an invalid governance field.");
  return field;
}

function mapExportView(input: unknown): ExportView {
  const value = asRecord(input);
  const scope = requiredString(value, "scope");
  const state = requiredString(value, "state");
  if (!["queued", "running", "ready", "expired", "failed", "deleted"].includes(state)) {
    throw invalidGovernanceContract("The server returned an invalid export state.");
  }
  const common = {
    id: requiredString(value, "id"),
    state: state as ExportView["state"],
    createdAt: requiredString(value, "createdAt"),
    ...(typeof value.expiresAt === "string" ? { expiresAt: value.expiresAt } : {}),
    version: requiredVersion(value),
    etag: requiredString(value, "etag"),
  };
  if (scope === "mailbox") {
    if (value.format !== "jsonl" || !["metadata", "authorized_content"].includes(String(value.contentMode))) {
      throw invalidGovernanceContract("The server returned an invalid mailbox export.");
    }
    return {
      ...common,
      scope,
      mailboxId: requiredString(value, "mailboxId"),
      format: "jsonl",
      contentMode: value.contentMode as "metadata" | "authorized_content",
    };
  }
  if (scope === "tenant" && value.mailboxId === null && value.format === "portable_bundle_v1" && value.contentMode === "authorized_content") {
    return { ...common, scope, mailboxId: null, format: "portable_bundle_v1", contentMode: "authorized_content" };
  }
  throw invalidGovernanceContract("The server returned an invalid tenant export.");
}

function mapOperationView(input: unknown): OperationView {
  const value = asRecord(input);
  const state = requiredString(value, "state");
  if (!["pending", "running", "succeeded", "failed", "cancelled", "ambiguous"].includes(state)) {
    throw invalidGovernanceContract("The server returned an invalid operation state.");
  }
  return {
    id: requiredString(value, "id"),
    kind: requiredString(value, "kind"),
    state: state as OperationView["state"],
    detail: requiredString(value, "detail"),
    updatedAt: requiredString(value, "updatedAt"),
  };
}

function mapDeletionIntentView(input: unknown): DeletionIntentView {
  const value = asRecord(input);
  const resourceType = requiredString(value, "resourceType");
  const state = requiredString(value, "state");
  if (!["tenant", "domain", "mailbox", "alias", "message"].includes(resourceType)) {
    throw invalidGovernanceContract("The server returned an invalid deletion resource type.");
  }
  if (!["preview_pending", "awaiting_confirmation", "blocked_legal_hold", "requested", "dependency_checked", "journal_pending", "journal_acked", "provider_deleting", "provider_confirmed", "objects_deleting", "tombstoned", "cancelled"].includes(state)) {
    throw invalidGovernanceContract("The server returned an invalid deletion state.");
  }
  const preview = value.preview === null ? null : mapDeletionPreview(value.preview);
  const requestedBy = mapDeletionPrincipal(value.requestedBy);
  const confirmedByValue = value.confirmedBy;
  const confirmedBy = confirmedByValue === null ? null : (() => {
    const principal = asRecord(confirmedByValue);
    return { ...mapDeletionPrincipal(principal), confirmedAt: requiredString(principal, "confirmedAt") };
  })();
  const duties = asRecord(value.separationOfDuties);
  const confirmationDigest = nullableString(value, "confirmationDigest");
  const confirmationExpiresAt = nullableString(value, "confirmationExpiresAt");
  if (confirmationDigest !== null && !/^sha256:[0-9a-f]{64}$/u.test(confirmationDigest)) {
    throw invalidGovernanceContract("The server returned an invalid deletion confirmation digest.");
  }
  if ((preview === null) !== (confirmationDigest === null) || (preview === null) !== (confirmationExpiresAt === null)) {
    throw invalidGovernanceContract("The server returned an inconsistent deletion preview.");
  }
  return {
    id: requiredString(value, "id"),
    resourceType: resourceType as DeletionIntentView["resourceType"],
    resourceId: requiredString(value, "resourceId"),
    state: state as DeletionIntentView["state"],
    preview,
    confirmationDigest,
    confirmationExpiresAt,
    requestedBy,
    confirmedBy,
    separationOfDuties: {
      required: requiredBoolean(duties, "required"),
      distinctPrincipalRequired: requiredBoolean(duties, "distinctPrincipalRequired"),
      requesterMayConfirm: requiredBoolean(duties, "requesterMayConfirm"),
    },
    createdAt: requiredString(value, "createdAt"),
    updatedAt: requiredString(value, "updatedAt"),
    version: requiredVersion(value),
    etag: requiredString(value, "etag"),
  };
}

function mapDeletionPrincipal(input: unknown): DeletionIntentView["requestedBy"] {
  const value = asRecord(input);
  const principalType = requiredString(value, "principalType");
  if (principalType !== "user" && principalType !== "api_client") {
    throw invalidGovernanceContract("The server returned an invalid deletion principal type.");
  }
  return { principalType, principalId: requiredString(value, "principalId") };
}

function mapDeletionPreview(input: unknown): NonNullable<DeletionIntentView["preview"]> {
  const value = asRecord(input);
  if (!Array.isArray(value.dependencyCounts) || !Array.isArray(value.retentionConstraints) || !Array.isArray(value.blockers)) {
    throw invalidGovernanceContract("The server returned an invalid deletion preview.");
  }
  const dependencyTypes = ["domain", "mailbox", "alias", "message", "attachment", "draft", "send_intent", "webhook", "api_client", "export", "provider_binding"] as const;
  const dispositions = ["delete", "detach", "revoke", "expire", "retain"] as const;
  const retentionClasses = ["audit", "backup", "tombstone_journal", "legal_hold"] as const;
  const reasonCodes = ["audit_integrity", "backup_expiry", "restore_safety", "active_legal_hold"] as const;
  const blockerCodes = ["active_legal_hold", "consumer_ack_pending", "dependency_conflict", "provider_unavailable"] as const;
  const dependencyCounts = value.dependencyCounts.map((entry) => {
    const item = asRecord(entry);
    const resourceType = requiredString(item, "resourceType");
    const disposition = requiredString(item, "disposition");
    if (!dependencyTypes.includes(resourceType as typeof dependencyTypes[number]) || !dispositions.includes(disposition as typeof dispositions[number])) {
      throw invalidGovernanceContract("The server returned an invalid deletion dependency.");
    }
    return {
      resourceType: resourceType as typeof dependencyTypes[number],
      count: requiredCount(item, "count"),
      disposition: disposition as typeof dispositions[number],
    };
  });
  const retentionConstraints = value.retentionConstraints.map((entry) => {
    const item = asRecord(entry);
    const className = requiredString(item, "class");
    const reasonCode = requiredString(item, "reasonCode");
    if (!retentionClasses.includes(className as typeof retentionClasses[number]) || !reasonCodes.includes(reasonCode as typeof reasonCodes[number])) {
      throw invalidGovernanceContract("The server returned an invalid retention constraint.");
    }
    return {
      class: className as typeof retentionClasses[number],
      reasonCode: reasonCode as typeof reasonCodes[number],
      retainUntil: nullableString(item, "retainUntil"),
    };
  });
  const legalHold = asRecord(value.legalHold);
  const acknowledgements = asRecord(value.consumerAcknowledgements);
  const blockers = value.blockers.map((entry) => {
    const item = asRecord(entry);
    const code = requiredString(item, "code");
    if (!blockerCodes.includes(code as typeof blockerCodes[number])) throw invalidGovernanceContract("The server returned an invalid deletion blocker.");
    return { code: code as typeof blockerCodes[number], count: requiredCount(item, "count") };
  });
  return {
    dependencyCounts,
    retentionConstraints,
    legalHold: {
      activeCount: requiredCount(legalHold, "activeCount"),
      blocksConfirmation: requiredBoolean(legalHold, "blocksConfirmation"),
    },
    consumerAcknowledgements: {
      requiredCount: requiredCount(acknowledgements, "requiredCount"),
      acknowledgedCount: requiredCount(acknowledgements, "acknowledgedCount"),
      pendingCount: requiredCount(acknowledgements, "pendingCount"),
      blockerCount: requiredCount(acknowledgements, "blockerCount"),
    },
    blockers,
    canConfirm: requiredBoolean(value, "canConfirm"),
    generatedAt: requiredString(value, "generatedAt"),
  };
}

function invalidGovernanceContract(detail: string): ApiProblem {
  return new ApiProblem(502, "invalid_governance_contract", detail, "browser");
}

function safeTransferUrl(value: string): string {
  const url = new URL(value, window.location.origin);
  if (url.protocol !== "https:" || url.username || url.password || url.hash) {
    throw new ApiProblem(502, "unsafe_transfer_url", "The server returned an unsafe file transfer address.", "browser");
  }
  return url.href;
}

function safeUploadHeaders(value: Record<string, string>): Headers {
  const headers = new Headers();
  for (const [name, headerValue] of Object.entries(value)) {
    const normalized = name.toLowerCase();
    if (normalized !== "content-type" && !normalized.startsWith("x-amz-")) {
      throw new ApiProblem(502, "unsafe_upload_header", "The server returned an unsafe attachment upload header.", "browser");
    }
    headers.set(name, headerValue);
  }
  return headers;
}

export function createSameOriginBffClient(fetchPort?: FetchPort): InboxFuryWebPort {
  return new SameOriginBffClient(fetchPort);
}
